/**
 * One image, at a size that suits the device.
 *
 * Strapi already generates smaller versions of every upload - `thumbnail`, `small`,
 * `medium`, `large` - and the public API passes them through as `formats`. Until now
 * nothing used them: every `<img>` on the site asked for the original, so a phone on a
 * mobile connection downloaded the same 2000px photograph as a desktop. This turns that
 * list into a `srcset` and lets the browser choose.
 *
 * Three things it does besides:
 *
 * - **`width` and `height` are always set**, from the original's intrinsic size. The CSS
 *   still decides how big the image is drawn; the attributes only tell the browser the
 *   aspect ratio, so the page does not jump when the image arrives. That is the cheapest
 *   Cumulative Layout Shift fix there is.
 * - **`loading` defaults to lazy**, except when `priority` is set. Exactly one image per
 *   page should be `priority`: the one visible before any scrolling, which is usually
 *   the Largest Contentful Paint element. Lazy-loading that one makes it slower, not
 *   faster.
 * - **`sizes` defaults to `100vw`**, which never under-fetches and therefore never picks
 *   a blurry candidate. Pass something tighter wherever the image is known to occupy a
 *   column - a four-up card row is `25vw` on a desktop, not `100vw`, and the difference
 *   is most of the bytes.
 */
import { mediaUrl } from '../../lib/config';
import type { StrapiMedia } from '../../types/sections';

export interface ImageProps {
  media?: StrapiMedia | null;
  alt?: string | null;
  /** How wide the image is drawn, so the browser can pick a candidate. */
  sizes?: string;
  className?: string;
  /** The one above-the-fold image: loaded eagerly and at high priority. */
  priority?: boolean;
}

/** Common `sizes` values, so the call sites do not each invent their own. */
export const SIZES = {
  full: '100vw',
  half: '(min-width: 640px) 50vw, 100vw',
  third: '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw',
  quarter: '(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw'
} as const;

/**
 * `url width` pairs for every version Strapi made, largest last, duplicates dropped.
 *
 * The original is included as a candidate: the generated formats stop at `large`
 * (1000px by default), which is not enough for a full-bleed band on a wide screen.
 */
function buildSrcSet(media: StrapiMedia): string | undefined {
  const byWidth = new Map<number, string>();

  for (const format of Object.values(media.formats ?? {})) {
    if (format?.url && format.width) byWidth.set(format.width, mediaUrl(format.url) ?? format.url);
  }
  if (media.url && media.width) byWidth.set(media.width, mediaUrl(media.url) ?? media.url);

  if (byWidth.size < 2) return undefined; // one candidate is not a choice
  return [...byWidth.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([width, url]) => `${url} ${width}w`)
    .join(', ');
}

export function Image({ media, alt, sizes = SIZES.full, className, priority = false }: ImageProps) {
  if (!media?.url) return null;

  return (
    <img
      src={mediaUrl(media.url)}
      srcSet={buildSrcSet(media)}
      sizes={sizes}
      width={media.width || undefined}
      height={media.height || undefined}
      alt={alt ?? media.alternativeText ?? ''}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      // React 18 does not know this attribute, hence the lower-case escape hatch.
      {...(priority ? { fetchpriority: 'high' } : {})}
      className={className}
    />
  );
}
